import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from './config.js';
import { Cohort } from './Cohort.js';
import { BlogMessage } from './BlogMessage.js';

// Collection names
const COHORTS_COLLECTION = 'cohorts';
const BLOG_MESSAGES_COLLECTION = 'blogMessages';

export const FIREBASE_NOT_CONFIGURED_MSG =
  'Firebase not configured. Copy .env.example to .env.local and add Firebase config (get values from your team or Firebase Console).';

function ensureDb() {
  if (!db) {
    throw new Error(FIREBASE_NOT_CONFIGURED_MSG);
  }
}

/**
 * Fetch all active cohorts
 * @returns {Promise<Cohort[]>} Array of active Cohort instances
 */
export const getActiveCohorts = async () => {
  ensureDb();
  try {
    const cohortsRef = collection(db, COHORTS_COLLECTION);
    const q = query(cohortsRef, 
      where('status', '==', 'Active'), 
      orderBy('cohortId', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const cohorts = [];
    
    querySnapshot.forEach((doc) => {
      cohorts.push(Cohort.fromFirestore(doc));
    });
    
    return cohorts;
  } catch (error) {
    console.error('Error fetching active cohorts:', error);
    throw error;
  }
};

/**
 * Fetch a single cohort by Firestore document ID
 * @param {string} cohortDocId - Firestore document ID
 * @returns {Promise<Cohort>} Cohort instance
 */
export const getCohortById = async (cohortDocId) => {
  ensureDb();
  try {
    const cohortRef = doc(db, COHORTS_COLLECTION, cohortDocId);
    const cohortSnap = await getDoc(cohortRef);
    
    if (cohortSnap.exists()) {
      return Cohort.fromFirestore(cohortSnap);
    } else {
      throw new Error('Cohort not found');
    }
  } catch (error) {
    console.error('Error fetching cohort by ID:', error);
    throw error;
  }
};

/**
 * Fetch a cohort by startup name
 * @param {string} startupName - Name of the startup
 * @returns {Promise<Cohort>} Cohort instance
 */
export const getCohortByStartupName = async (startupName) => {
  ensureDb();
  try {
    const cohortsRef = collection(db, COHORTS_COLLECTION);
    const q = query(
      cohortsRef, 
      where('startupName', '==', startupName)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return Cohort.fromFirestore(doc);
    } else {
      throw new Error(`Cohort not found for startup: ${startupName}`);
    }
  } catch (error) {
    console.error('Error fetching cohort by startup name:', error);
    throw error;
  }
};

/**
 * Create a new cohort
 * @param {Object} cohortData - startupName, founderNames, status, email, semester
 * @returns {Promise<string>} Firestore document ID
 */
export const createCohort = async (cohortData) => {
  ensureDb();
  try {
    let cohortId = cohortData.cohortId;
    if (!cohortId) {
      const cohortsRef = collection(db, COHORTS_COLLECTION);
      const q = query(cohortsRef, orderBy('cohortId', 'desc'));
      const querySnapshot = await getDocs(q);
      let maxCohortId = 0;
      if (!querySnapshot.empty) {
        const allCohorts = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.cohortId && typeof data.cohortId === 'number') {
            allCohorts.push(data.cohortId);
          }
        });
        maxCohortId = allCohorts.length > 0 ? Math.max(...allCohorts) : 0;
      }
      cohortId = maxCohortId + 1;
    }

    const cohort = new Cohort({
      ...cohortData,
      cohortId,
      semester: cohortData.semester || 'SP26',
    });

    const cohortsRef = collection(db, COHORTS_COLLECTION);
    const docRef = await addDoc(cohortsRef, cohort.toFirestore());
    return docRef.id;
  } catch (error) {
    console.error('Error creating cohort:', error);
    throw error;
  }
};

/**
 * Update a cohort
 * @param {string} cohortDocId - Firestore document ID
 * @param {Object} updates - Fields to update
 */
export const updateCohort = async (cohortDocId, updates) => {
  ensureDb();
  try {
    const cohortRef = doc(db, COHORTS_COLLECTION, cohortDocId);
    await updateDoc(cohortRef, updates);
  } catch (error) {
    console.error('Error updating cohort:', error);
    throw error;
  }
};

/**
 * Delete a cohort
 * @param {string} cohortDocId - Firestore document ID
 */
export const deleteCohort = async (cohortDocId) => {
  ensureDb();
  try {
    const cohortRef = doc(db, COHORTS_COLLECTION, cohortDocId);
    await deleteDoc(cohortRef);
  } catch (error) {
    console.error('Error deleting cohort:', error);
    throw error;
  }
};

/**
 * Fetch all blog messages for a specific cohort (by cohortId number)
 * @param {number} cohortId - The cohortId number (not Firestore doc ID)
 * @returns {Promise<BlogMessage[]>} Array of BlogMessage instances, sorted by date (newest first)
 */
export const getBlogMessagesByCohortId = async (cohortId) => {
  ensureDb();
  try {
    const messagesRef = collection(db, BLOG_MESSAGES_COLLECTION);
    const q = query(
      messagesRef,
      where('cohortId', '==', cohortId),
      orderBy('postedDate', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push(BlogMessage.fromFirestore(doc));
    });
    
    return messages;
  } catch (error) {
    console.error('Error fetching blog messages:', error);
    throw error;
  }
};

/**
 * Fetch a single blog message by Firestore document ID
 * @param {string} messageDocId - Firestore document ID
 * @returns {Promise<BlogMessage>} BlogMessage instance
 */
export const getBlogMessageById = async (messageDocId) => {
  ensureDb();
  try {
    const messageRef = doc(db, BLOG_MESSAGES_COLLECTION, messageDocId);
    const messageSnap = await getDoc(messageRef);
    
    if (messageSnap.exists()) {
      return BlogMessage.fromFirestore(messageSnap);
    } else {
      throw new Error('Blog message not found');
    }
  } catch (error) {
    console.error('Error fetching blog message:', error);
    throw error;
  }
};

/**
 * Create a new blog message
 * @param {Object} messageData - Blog message data
 * @returns {Promise<string>} Firestore document ID
 */
export const createBlogMessage = async (messageData) => {
  ensureDb();
  try {
    const message = new BlogMessage({
      ...messageData,
      postedDate: messageData.postedDate || new Date(),
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    // Convert Date objects to Firestore Timestamps
    const toTimestamp = (date) => {
      if (!date) return null;
      if (date instanceof Date) {
        return Timestamp.fromDate(date);
      }
      if (date.toDate) {
        return date; // Already a Timestamp
      }
      return Timestamp.fromDate(new Date(date));
    };
    
    const firestoreData = message.toFirestore();
    firestoreData.postedDate = toTimestamp(firestoreData.postedDate);
    firestoreData.createdAt = toTimestamp(firestoreData.createdAt);
    firestoreData.updatedAt = toTimestamp(firestoreData.updatedAt);
    
    const messagesRef = collection(db, BLOG_MESSAGES_COLLECTION);
    const docRef = await addDoc(messagesRef, firestoreData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating blog message:', error);
    throw error;
  }
};

/**
 * Update a blog message
 * @param {string} messageDocId - Firestore document ID
 * @param {Object} updates - Fields to update
 */
export const updateBlogMessage = async (messageDocId, updates) => {
  ensureDb();
  try {
    const messageRef = doc(db, BLOG_MESSAGES_COLLECTION, messageDocId);
    await updateDoc(messageRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating blog message:', error);
    throw error;
  }
};

/**
 * Delete a blog message
 * @param {string} messageDocId - Firestore document ID
 */
export const deleteBlogMessage = async (messageDocId) => {
  ensureDb();
  try {
    const messageRef = doc(db, BLOG_MESSAGES_COLLECTION, messageDocId);
    await deleteDoc(messageRef);
  } catch (error) {
    console.error('Error deleting blog message:', error);
    throw error;
  }
};

/**
 * Get email newsletter content with all active cohorts and their latest blog posts
 * @returns {Promise<Object>} Object containing formatted email content
 */
export const getNewsletterContent = async () => {
  ensureDb();
  try {
    const cohorts = await getActiveCohorts();
    
    // Get latest blog post for each cohort
    const newsletterItems = await Promise.all(
      cohorts.map(async (cohort) => {
        let latestPost = null;
        try {
          const messages = await getBlogMessagesByCohortId(cohort.cohortId);
          if (messages.length > 0) {
            const latestMessage = messages[0]; // Already sorted by date desc
            latestPost = {
              title: latestMessage.messageTitle || 'Untitled',
              content: latestMessage.messageBody || '',
              date: latestMessage.postedDate || new Date(),
            };
          }
        } catch (err) {
          console.error(`Error fetching messages for cohort ${cohort.cohortId}:`, err);
        }

        return {
          startupName: cohort.startupName,
          founderNames: cohort.founderNames,
          email: cohort.email,
          latestPost: latestPost,
        };
      })
    );

    // Format email content
    const emailSubject = `Momentum Blog Updates - ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    
    let emailBody = '<h1>Momentum Blog Updates</h1>\n\n';
    
    newsletterItems.forEach((item) => {
      emailBody += `<h2>${item.startupName}</h2>\n`;
      if (item.founderNames) {
        emailBody += `<p><strong>Founders:</strong> ${item.founderNames}</p>\n`;
      }
      
      if (item.latestPost) {
        emailBody += `<h3>${item.latestPost.title}</h3>\n`;
        emailBody += `<p><em>Posted: ${new Date(item.latestPost.date).toLocaleDateString()}</em></p>\n`;
        // Truncate content to 500 characters for email
        const preview = item.latestPost.content.length > 500 
          ? item.latestPost.content.substring(0, 500) + '...' 
          : item.latestPost.content;
        emailBody += `<p>${preview.replace(/\n/g, '<br>')}</p>\n`;
      } else {
        emailBody += `<p><em>No blog posts yet.</em></p>\n`;
      }
      emailBody += '<hr>\n\n';
    });

    return {
      subject: emailSubject,
      htmlBody: emailBody,
      textBody: emailBody.replace(/<[^>]*>/g, ''), // Plain text version
      items: newsletterItems,
    };
  } catch (error) {
    console.error('Error generating newsletter content:', error);
    throw error;
  }
};

/**
 * Send newsletter email to email list
 * @param {string[]} recipientEmails - Array of email addresses to send to
 * @param {Function} emailService - Email sending function (e.g., EmailJS, SendGrid, etc.)
 * @returns {Promise<void>}
 */
export const sendNewsletterEmail = async (recipientEmails, emailService) => {
  ensureDb();
  try {
    if (!Array.isArray(recipientEmails) || recipientEmails.length === 0) {
      throw new Error('Recipient email list is required and must not be empty');
    }

    if (typeof emailService !== 'function') {
      throw new Error('Email service function is required');
    }

    // Get newsletter content
    const newsletterContent = await getNewsletterContent();

    // Send email to each recipient
    const emailPromises = recipientEmails.map((email) => 
      emailService({
        to: email,
        subject: newsletterContent.subject,
        html: newsletterContent.htmlBody,
        text: newsletterContent.textBody,
      })
    );

    await Promise.all(emailPromises);
    console.log(`Newsletter sent successfully to ${recipientEmails.length} recipients`);
  } catch (error) {
    console.error('Error sending newsletter email:', error);
    throw error;
  }
};
